import { AlertBar } from '#GameFrontend/components/ui/AlertBar/AlertBar';
import { Button } from '#GameFrontend/components/ui/button';
import { Input } from '#GameFrontend/components/ui/input';
import { Label } from '#GameFrontend/components/ui/label';
import { socket } from '#GameFrontend/services/socket';
import { RuleSet, validate } from '#GameFrontend/unauthorized/lib/Validation';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

const Login = () => {
	const { t } = useTranslation();
	const [username, setUsername] = useState('');
	const [password, setPassword] = useState('');
	const [rememberMe, setRememberMe] = useState(false);
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [success, setSuccess] = useState<string | null>(null);
	const [isAlertVisible, setIsAlertVisible] = useState<boolean>(false);
	const [showResendLink, setShowResendLink] = useState(false);
	const [showPasswordResetLink, setShowPasswordResetLink] = useState(false);
	const ruleSet = useRef<{ [key: string]: RuleSet } | undefined>(undefined);

	useEffect(() => {
		// Get validation configuration from server
		socket.timeout(5000).emit('User:Login:ValidationConfig', {});

		socket.on('User:Login:ValidationConfig', (data) => {
			if (data.success) {
				ruleSet.current = data.data as { [key: string]: RuleSet };
			} else {
				setError(t('load.config.error'));
				setIsAlertVisible(true);
			}
		});

		socket.on('User:Login', (data) => {
			if (!data.success) {
				if (data.translation[0].code === 2) {
					// Email not verified - show resend link
					setError(t(data.translation[0].key));
					setShowResendLink(true);
				} else {
					setError(t(data.translation[0].key));
					setShowPasswordResetLink(true);
				}
				setIsAlertVisible(true);
			}
		});

		socket.on('User:ResendMail', (data) => {
			if (data.success) {
				setError(null);
				setSuccess(t('email.resend.success'));
			} else {
				setError(t(data.translation[0].key));
			}
			setIsAlertVisible(true);
		});

		socket.on('User:PasswordResetMail', (data) => {
			if (data.success) {
				setError(null);
				setSuccess(t('email.resend.success'));
			} else {
				setError(t(data.translation[0].key));
			}
			setIsAlertVisible(true);
		});

		return () => {
			socket.off('User:Login:ValidationConfig');
			socket.off('User:Login');
			socket.off('User:ResendMail');
			socket.off('User:PasswordResetMail');
		};
	}, [t]);

	const validateForm = () => {
		if (!ruleSet.current) {
			setError(t('load.config.error'));
			setIsAlertVisible(true);
			return false;
		}

		const errorList = [];

		// Validate username using the rule set
		const usernameValidation = validate('username', ruleSet.current.username, username);
		if (!usernameValidation.valid) {
			errorList.push(usernameValidation.message);
		}

		// Validate password using the rule set
		const passwordValidation = validate('password', ruleSet.current.password, password);
		if (!passwordValidation.valid) {
			errorList.push(passwordValidation.message);
		}

		if (errorList.length > 0) {
			setError(errorList.join('\n'));
			setIsAlertVisible(true);
			return false;
		}

		return true;
	};

	const handleResend = () => {
		if (isLoading) return;

		setIsLoading(true);
		socket.timeout(5000).emit('User:ResendMail', { username }, () => {
			setIsLoading(false);
		});
	};

	const handlePasswordReset = () => {
		if (isLoading) return;

		setIsLoading(true);
		socket.timeout(5000).emit('User:PasswordResetMail', { username }, () => {
			setIsLoading(false);
		});
	};

	const handleToggle = (e: React.MouseEvent) => {
		e.preventDefault();
		setRememberMe(!rememberMe);
	};

	const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
		event.preventDefault();

		if (!validateForm()) {
			return;
		}

		setIsLoading(true);
		socket.timeout(5000).emit(
			'User:Login',
			{
				username: username,
				password: password,
				remember: rememberMe,
			},
			() => {
				setIsLoading(false);
			}
		);
	};

	const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const { name, value, type, checked } = e.target;

		if (name === 'username') {
			setUsername(value);
		} else if (name === 'password') {
			setPassword(value);
		} else if (name === 'rememberMe') {
			setRememberMe(checked);
		}

		// Clear error when input changes
		setError(null);
		setIsAlertVisible(false);
	};

	return (
		<>
			{success && (
				<AlertBar
					type="success"
					message={success}
					isVisible={isAlertVisible}
					setIsVisible={setIsAlertVisible}
				/>
			)}
			{error && (
				<AlertBar
					type="error"
					message={error}
					showEventLink={showResendLink || showPasswordResetLink}
					eventLinkText={showResendLink ? t('email.resend.button') : t('password.reset.button')}
					eventLinkFn={showResendLink ? handleResend : handlePasswordReset}
					isVisible={isAlertVisible}
					setIsVisible={setIsAlertVisible}
				/>
			)}
			<div>
				<form className="relative" onSubmit={handleSubmit}>
					<div className="wood-light p-6 pb-6 rounded-[15px] shadow-md">
						<div className="space-y-2 relative">
							<Label htmlFor="username" className="text-white text-lg">
								{t('username')}
							</Label>
							<Input
								id="username"
								name="username"
								type="text"
								value={username}
								onChange={handleInputChange}
								className="bg-white"
							/>
						</div>
						<div className="space-y-2 relative mt-2">
							<Label htmlFor="password" className="text-white text-lg">
								{t('password')}
							</Label>
							<Input
								id="password"
								name="password"
								type="password"
								value={password}
								onChange={handleInputChange}
								className="bg-white"
							/>
						</div>
						<div className="flex items-center justify-center space-x-2 space-y-2 mt-2 relative">
							<input
								type="checkbox"
								id="remember"
								name="rememberMe"
								className="toggle-switch-input sr-only"
								checked={rememberMe}
								onChange={handleInputChange}
							/>
							<label
								htmlFor="remember"
								className="flex items-center cursor-pointer"
								onClick={handleToggle}
							>
								<span className="slider"></span>
								<span className="ml-2 text-white text-lg">{t('login.remember_me')}</span>
							</label>
						</div>

						<div className="flex justify-center lg:hidden space-y-2 mt-4 relative">
							<Button
								disabled={isLoading}
								className="w-full lg:w-[120px] orange-solid-button rounded-[10px] lg:text-base shadow-md text-lg"
							>
								{t('login.button')}
							</Button>
						</div>
					</div>
					<div className="hidden lg:flex justify-center mt-0 pt-0">
						<Button
							disabled={isLoading}
							className="w-full lg:w-[120px] orange-solid-button rounded-[10px] lg:text-base shadow-md rounded-t-none border-t-0"
						>
							{t('login.button')}
						</Button>
					</div>
				</form>
			</div>
		</>
	);
};

export default Login;
